import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Star, Quote, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-rotating display of approved app reviews.
// variant "hero"      -> just the rotating card
// variant "hero-rail" -> a permanent average-rating line + the rotating card (sits under the promo box)
// variant "section"   -> a headed testimonials section
// Clicking the card opens a scrollable dialog with every approved review.
// Renders nothing until at least one approved review exists.
export function ReviewCarousel({ variant = 'hero', refreshKey }) {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/app-reviews/approved`).then(r => setReviews(r.data || [])).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    if (reviews.length <= 1) return;
    timer.current = setInterval(() => setIdx(i => (i + 1) % reviews.length), 5000);
    return () => clearInterval(timer.current);
  }, [reviews.length]);

  if (!reviews.length) return null;
  const r = reviews[idx % reviews.length];
  const showDots = reviews.length > 1 && reviews.length <= 6;
  const rawAvg = reviews.reduce((s, x) => s + (x.rating || 0), 0) / reviews.length;
  // Public rule: show 5.0 only when every review is a perfect 5.0, otherwise cap at 4.9.
  const publicAvg = rawAvg >= 5 ? 5 : 4.9;

  const card = (
    <div data-testid="review-carousel" onClick={() => setOpen(true)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
      title="Read all reviews"
      className="relative glass-panel rounded-2xl p-4 overflow-hidden min-h-[136px] cursor-pointer transition-shadow hover:shadow-lg hover:ring-2 hover:ring-violet-200">
      <Quote className="absolute top-3 right-3 w-6 h-6 text-violet-200" fill="#ddd6fe" />
      <div key={idx} className="animate-fade-in">
        <div className="flex items-center gap-0.5 mb-1.5" data-testid="carousel-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-amber-400' : 'text-slate-200'}`} fill={n <= r.rating ? '#fbbf24' : 'none'} />
          ))}
        </div>
        <p className="text-sm text-slate-700 leading-snug line-clamp-3 pr-6">&ldquo;{r.review_text}&rdquo;</p>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="font-semibold text-slate-800">{r.full_name || r.first_name}</span>
          <span className="text-slate-300">·</span>
          <span className="text-violet-500 font-medium">{r.profile_type}</span>
          <span className="ml-auto text-[10px] text-violet-400 font-medium pr-1 hidden sm:inline">Read all →</span>
        </div>
        {r.features?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {r.features.slice(0, 4).map((f, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 whitespace-nowrap">{f}</span>
            ))}
          </div>
        )}
      </div>
      {showDots && (
        <div className="absolute bottom-2.5 right-3 flex gap-1">
          {reviews.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === (idx % reviews.length) ? 'bg-violet-400' : 'bg-slate-200'}`} />
          ))}
        </div>
      )}
    </div>
  );

  const allReviewsDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 gap-0 rounded-3xl overflow-hidden" data-testid="review-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <DialogTitle className="text-lg text-slate-900" style={{ fontFamily: 'Fredoka' }}>What our community says</DialogTitle>
          <div className="flex items-center gap-2 text-sm pt-1" data-testid="review-dialog-summary">
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(n => (
                <Star key={n} className={`w-4 h-4 ${n <= Math.round(publicAvg) ? 'text-amber-400' : 'text-slate-300'}`} fill={n <= Math.round(publicAvg) ? '#fbbf24' : 'none'} />
              ))}
            </span>
            <span className="font-bold text-slate-900" style={{ fontFamily: 'Fredoka' }}>{publicAvg.toFixed(1)}</span>
            <span className="text-slate-500">· {reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-3" data-testid="review-dialog-list">
          {reviews.map((rv, i) => (
            <div key={i} data-testid={`review-dialog-item-${i}`} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3.5">
              <div className="flex items-center gap-0.5 mb-1.5">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= rv.rating ? 'text-amber-400' : 'text-slate-200'}`} fill={n <= rv.rating ? '#fbbf24' : 'none'} />
                ))}
              </div>
              <p className="text-sm text-slate-700 leading-snug">&ldquo;{rv.review_text}&rdquo;</p>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="font-semibold text-slate-800">{rv.full_name || rv.first_name}</span>
                <span className="text-slate-300">·</span>
                <span className="text-violet-500 font-medium">{rv.profile_type}</span>
              </div>
              {rv.features?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {rv.features.slice(0, 5).map((f, j) => (
                    <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 whitespace-nowrap">{f}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );

  const ratingFixture = (
    <div className="flex items-center justify-center gap-2 text-sm cursor-pointer rounded-full py-1 transition-colors hover:bg-white/40" data-testid="review-rating-fixture"
      onClick={() => setOpen(true)} role="button" tabIndex={0} title="Read all reviews"
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}>
      <span className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(n => (
          <Star key={n} className={`w-4 h-4 ${n <= Math.round(publicAvg) ? 'text-amber-400' : 'text-slate-300'}`} fill={n <= Math.round(publicAvg) ? '#fbbf24' : 'none'} />
        ))}
      </span>
      <span className="font-bold text-slate-900" style={{ fontFamily: 'Fredoka' }}>{publicAvg.toFixed(1)}</span>
      <span className="text-slate-500">· {reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
    </div>
  );

  if (variant === 'rating-only') {
    return (
      <div data-testid="review-rating-only">
        {ratingFixture}
        {allReviewsDialog}
      </div>
    );
  }

  if (variant === 'hero-rail') {
    return (
      <div className="space-y-2.5" data-testid="review-hero-rail">
        {ratingFixture}
        {card}
        {allReviewsDialog}
      </div>
    );
  }

  if (variant === 'section') {
    return (
      <section className="relative z-10 px-6 py-16" data-testid="review-carousel-section">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-sm font-medium uppercase tracking-widest mb-3" style={{ background: 'linear-gradient(135deg, #8b5cf6, #14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Loved by our community</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Fredoka' }}>What our users say</h2>
        </div>
        <div className="max-w-xl mx-auto">{card}</div>
        {allReviewsDialog}
      </section>
    );
  }

  return <>{card}{allReviewsDialog}</>;
}

export default ReviewCarousel;
